"use client";
import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { NotificationService } from "../../../service/NotificationService";
import { AppDispatch } from "../../../feature/store/store";
import { confirmEmailVerificationCodeApi } from "../../../feature/reducers/userSlice";
import { inputClass, labelClass, errorClass, buttonClass, errorText } from "./style";

interface VerificationCodeFormProps {
  onNextStep: () => void;
  onBack?: () => void; // zurück zu Schritt 1 (Code erneut senden)
  email: string;
}

const VerificationCode: React.FC<VerificationCodeFormProps> = ({ onNextStep, onBack, email }) => {
  const dispatch = useDispatch<AppDispatch>();

  const formik = useFormik({
    initialValues: { code: "" },
    validationSchema: Yup.object({
      code: Yup.string()
        .matches(/^\d{6}$/, "Der Code hat 6 Ziffern")
        .required("Bitte Code eingeben"),
    }),
    onSubmit: async (values) => {
      try {
        await dispatch(confirmEmailVerificationCodeApi({ email, verificationCode: values.code })).unwrap();
        NotificationService.success("Code bestätigt!");
        onNextStep();
      } catch (error: any) {
        NotificationService.error(errorText(error, "Der Code ist ungültig oder abgelaufen."));
      }
    },
  });

  const error = formik.touched.code && formik.errors.code;

  return (
    <form onSubmit={formik.handleSubmit} noValidate className="flex flex-col gap-5">
      <p className="text-[15px] leading-relaxed text-ao-muted">
        Wir haben einen Code an <strong className="break-all text-ao-fg">{email}</strong> geschickt.
        Schauen Sie auch im Spam-Ordner nach.
      </p>

      <div className="flex flex-col gap-2">
        <label htmlFor="code" className={labelClass}>Bestätigungscode</label>
        <input
          type="text"
          id="code"
          name="code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder="000000"
          value={formik.values.code}
          onChange={(e) => formik.setFieldValue("code", e.target.value.replace(/\D/g, ""))}
          onBlur={formik.handleBlur}
          aria-invalid={!!error}
          className={`${inputClass} text-center text-2xl font-bold tracking-[0.4em] ${
            error ? "border-red-500" : "border-ao-line-strong"
          }`}
        />
        {error && <p className={errorClass}>{formik.errors.code}</p>}
      </div>

      <button type="submit" disabled={formik.isSubmitting} className={buttonClass}>
        {formik.isSubmitting ? "Wird geprüft…" : "Code bestätigen"}
      </button>

      {onBack && (
        <button type="button" onClick={onBack} className="text-sm font-semibold text-ao-accent-fg hover:underline">
          Keinen Code bekommen? Erneut senden
        </button>
      )}
    </form>
  );
};

export default VerificationCode;
